// The atmosphere's beam table on the GPU, and the sunlight on a cloud read from it.
//
// A cloud is not where the observer is. Its light is the star's beam through the air above the cloud's own height, with the star's cosine at the cloud's own place on the curved planet:
// looking toward the star the cloud is further along the planet so the star is higher there, looking away it is lower, a high deck is still in sunlight when the ground is not (and the
// light that reaches it has grazed the upper air, so it is orange to red), and the planet's shadow is where the cosine passes the horizon at that height. All of that is one table
// look-up by (height, cosine), the same table the CPU reader holds (atmospherePhysics.ts trans3), so there is no per-layer table and no scalar cover standing in for the air.
//
// The table is the message's `trans` (t_r x t_mu, a colour) as its natural log, which is what is interpolated (the beam falls by orders of magnitude across a cell near the horizon):
// x runs over the cosine from the zenith (square-root spaced from the horizon of that height up), y over the height (h = H u^2). Hardware bilinear in that index space is what the CPU does.
import * as THREE from 'three';
import type { Atmosphere } from './atmospherePhysics';

/** Half the sun's angular width (rad): the planet's shadow edge is softened over that much by three taps. */
export const SUN_TAP = 0.0047;

/** The log-transmittance table as a texture (x: cosine axis, y: height axis). `null` makes a one-texel table that is dark everywhere (no atmosphere yet: no light on a cloud). */
export function makeTransTexture(A: Atmosphere | null): THREE.DataTexture {
  const w = A ? A.tMu : 1, h = A ? A.tR : 1;
  const data = new Uint16Array(w * h * 4);
  const one = THREE.DataUtils.toHalfFloat(1);
  for (let i = 0; i < w * h; i++) {
    for (let k = 0; k < 3; k++) data[i * 4 + k] = THREE.DataUtils.toHalfFloat(A ? A.lnT[i * 3 + k]! : -27.6);
    data[i * 4 + 3] = one;
  }
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false; tex.needsUpdate = true;
  return tex;
}

/**
 * `cloudBeam(d, dist)`: the star's beam (a colour relative to the beam outside the air) on a cloud that the eye sees along the unit direction `d` at distance `dist`, in render axes (y up
 * at the eye). The including shader declares `uSunDir` (unit, toward the star) first. `uAtmG` = (planet radius, air height, table rows, table columns), `uEyeH` the eye's height above
 * the planet and `uEyeR` its radius.
 */
export const CLOUD_BEAM_GLSL = /* glsl */`
uniform sampler2D uAtmT;     // ln of the beam's transmittance: x over the cosine from the zenith, y over height, rgb
uniform vec4  uAtmG;         // planet radius, air height, rows (heights), columns (cosines)
uniform float uEyeH;         // the eye's height above the planet (m)
uniform float uEyeR;         // and its radius from the centre
vec3 atmTrans(float h, float mu) {
  float r = uAtmG.x + h;
  float mh = -sqrt(max(h * (2.0 * uAtmG.x + h), 0.0)) / r;                 // the cosine of that height's horizon (h (2R + h) is 1 - (R/r)^2 without the cancelling)
  if (mu < mh) return vec3(0.0);
  float tx = sqrt(clamp((mu - mh) / (1.0 - mh), 0.0, 1.0)) * (uAtmG.w - 1.0);
  float ty = sqrt(clamp(h / uAtmG.y, 0.0, 1.0)) * (uAtmG.z - 1.0);
  vec3 v = texture2D(uAtmT, vec2((tx + 0.5) / uAtmG.w, (ty + 0.5) / uAtmG.z)).rgb;
  return exp(v) * step(vec3(-27.6), v);
}
vec3 cloudBeam(vec3 d, float dist) {
  vec3 p = d * dist;                                                       // from the eye
  float yc = uEyeR + p.y, hz = p.x * p.x + p.z * p.z;
  float rc = sqrt(yc * yc + hz);
  float hc = uEyeH + p.y + hz / (rc + yc);                                 // the cloud's height above the planet, without the cancelling of two radii
  float mu = (dot(uSunDir, p) + uSunDir.y * uEyeR) / rc;                   // the star's cosine from the zenith at the cloud's own place
  float sq = sqrt(max(1.0 - mu * mu, 0.0));
  const float cT = ${Math.cos(SUN_TAP).toFixed(8)}, sT = ${Math.sin(SUN_TAP).toFixed(8)};
  return 0.25 * (atmTrans(hc, mu * cT - sq * sT) + 2.0 * atmTrans(hc, mu) + atmTrans(hc, mu * cT + sq * sT));
}
`;
